// ============================================================
//  ROUTE BUILDER — Drag & Drop 50 Route Slots
//  Slot assignments persist to Firebase RTDB under rb_slots/{date}
//  Date is driven by the shared header arrows (viewingDate)
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from "react";
import { ref, set as fbSet, onValue, off } from "firebase/database";
import { db } from "../../firebase/config";
import { useProfileStore } from "../../store/profileStore";
import { useCrewStore, makeCrew } from "../../store/crewStore";
import { showToast } from "../shared/Toast";
import { getDateInfo } from "../../utils";
import type { Profile } from "../../types";

// ─── Types ───────────────────────────────────────────────────
interface SlotData {
  driverId: string | null;
  sidemanId: string | null;
  startTime: string;
  note: string;
  driverNote: string;
  sidemanNote: string;
  crewOff: Array<{ id: string; label: "D" | "S" }>;
  standby: Array<{ id: string; label: "D" | "S" }>;
  timeDuration: string;
  sideman2Id: string | null;
  sideman2Note: string;
  routeLabel?: string;
}

type DotStatus = "empty" | "pending" | "confirmed";

// ─── Dot styles ──────────────────────────────────────────────
const DOT: Record<DotStatus, { bg: string; shadow: string; title: string }> = {
  empty: { bg: "#3a3c41", shadow: "none", title: "No one assigned" },
  pending: {
    bg: "#e05252",
    shadow: "0 0 7px rgba(224,82,82,0.9)",
    title: "Awaiting acceptance",
  },
  confirmed: {
    bg: "#3ecf6e",
    shadow: "0 0 9px rgba(62,207,110,0.9)",
    title: "Accepted ✓",
  },
};

// ─── Helpers ─────────────────────────────────────────────────
function fmt12(t: string): string {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}
function emptySlots(count = 50): SlotData[] {
  return Array.from({ length: count }, () => ({
    driverId: null,
    sidemanId: null,
    startTime: "07:00",
    note: "",
    driverNote: "",
    sidemanNote: "",
    crewOff: [],
    standby: [],
    timeDuration: "",
    sideman2Id: null,
    sideman2Note: "",
  }));
}
function fbKey(date: string) {
  return `rb_slots/${date.replace(/\./g, "-")}`;
}
function routeLabel(slot: { routeLabel?: string }, idx: number): string {
  return slot.routeLabel?.trim() || `Route ${idx + 1}`;
}

// ─── Main Component ───────────────────────────────────────────
const RouteBuilder: React.FC = () => {
  const profiles = useProfileStore((s) => s.profiles);
  const { crews, addCrew, removeCrew, viewingDate, setViewingDate } =
    useCrewStore();

  // ── date is always the shared header date — no internal date state ──
  // Use local date to avoid UTC timezone shift (toISOString gives wrong date in BST)
  const date = `${viewingDate.getFullYear()}-${String(viewingDate.getMonth() + 1).padStart(2, "0")}-${String(viewingDate.getDate()).padStart(2, "0")}`;

  const [slots, setSlots] = useState<SlotData[]>(emptySlots);
  const [totalSlots, setTotalSlots] = useState(50);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [driversOpen, setDriversOpen] = useState(true);
  const [sidemenOpen, setSidemenOpen] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [overlayProfileId, setOverlayProfileId] = useState<string | null>(null);
  const [routeSearch, setRouteSearch] = useState("");
  const dragProfile = useRef<Profile | null>(null);
  const dragSource = useRef<{
    type: "panel" | "slot" | "crewOff" | "standby";
    slotIdx?: number;
    role?: "driver" | "sideman" | "sideman2";
  } | null>(null);
  const [lockPassword, setLockPassword] = useState<string>("");
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockInput, setLockInput] = useState("");
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [sessionUnlocked, setSessionUnlocked] = useState(false);
  const [statusOverlay, setStatusOverlay] = useState<
    | "driversAccepted"
    | "sidemenAccepted"
    | "driversPending"
    | "sidemenPending"
    | null
  >(null);

  // Load lock password from Firebase
  useEffect(() => {
    const passRef = ref(db, "config/routeLockPassword");
    const unsub = onValue(passRef, (snap) => {
      if (snap.val()) setLockPassword(snap.val());
    });
    return () => unsub();
  }, []);

  const isRouteLocked = (): boolean => {
    const ukNow = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Europe/London" }),
    );
    const viewingDateStr = date;
    const todayStr = `${ukNow.getFullYear()}-${String(ukNow.getMonth() + 1).padStart(2, "0")}-${String(ukNow.getDate()).padStart(2, "0")}`;
    if (viewingDateStr < todayStr) return true; // past dates always locked
    if (viewingDateStr > todayStr) return false; // future dates never locked
    return ukNow.getHours() >= 17; // today: locked after 5pm UK
  };

  const withLockCheck = (action: () => void) => {
    if (!isRouteLocked() || sessionUnlocked) {
      action();
      return;
    }
    setPendingAction(() => action);
    setLockInput("");
    setShowLockModal(true);
  };

  // Reset session unlock whenever the viewed date changes
  useEffect(() => {
    setSessionUnlocked(false);
  }, [date]);

  // ── Declare early so effects can reference them ───────────────
  const di = getDateInfo(new Date(date + "T00:00:00"));
  const savedCrews = crews.filter((c) => c.workDate === di.shortStr);

  // ── Persist slots to Firebase ─────────────────────────────────
  const saveToFirebase = useCallback(
    (newSlots: SlotData[]) => {
      fbSet(ref(db, fbKey(date)), newSlots).catch((e) =>
        console.warn("RB save error", e),
      );
    },
    [date],
  );

  // ── Firebase sync: load slots for selected date ───────────────
  useEffect(() => {
    setLoaded(false);
    const slotRef = ref(db, fbKey(date));
    const unsub = onValue(slotRef, (snap) => {
      const raw = snap.val();
      let loaded: SlotData[] = [];
      if (raw != null) {
        if (Array.isArray(raw) && raw.length > 0) {
          loaded = raw;
        } else if (typeof raw === "object") {
          const keys = Object.keys(raw)
            .map(Number)
            .filter((n) => !isNaN(n));
          if (keys.length > 0) {
            const maxIdx = Math.max(...keys);
            loaded = Array.from(
              { length: maxIdx + 1 },
              (_, i) =>
                raw[i] ?? {
                  driverId: null,
                  sidemanId: null,
                  startTime: "07:00",
                  note: "",
                  driverNote: "",
                  sidemanNote: "",
                  crewOff: [],
                  standby: [],
                  timeDuration: "",
                  sideman2Id: null,
                  sideman2Note: "",
                },
            );
          } else {
            loaded = emptySlots(50);
          }
        } else {
          loaded = emptySlots(50);
        }
      } else {
        // null from Firebase — could be auth timing, don't wipe existing state
        setLoaded(true);
        return;
      }
      const maxRoute = Math.max(50, loaded.length);
      while (loaded.length < maxRoute)
        loaded.push({
          driverId: null,
          sidemanId: null,
          startTime: "07:00",
          note: "",
          driverNote: "",
          sidemanNote: "",
          crewOff: [],
          standby: [],
          timeDuration: "",
          sideman2Id: null,
          sideman2Note: "",
        });
      // Back-fill per-role note fields for older records
      loaded = loaded.map((s) => ({
        ...s,
        driverNote: s.driverNote ?? "",
        sidemanNote: s.sidemanNote ?? "",
        crewOff: s.crewOff ?? [],
        standby: (s as any).standby ?? [],
        timeDuration: s.timeDuration ?? "",
        sideman2Id: s.sideman2Id ?? null,
        sideman2Note: s.sideman2Note ?? "",
      }));
      setTotalSlots(loaded.length);
      setSlots(loaded);
      setLoaded(true);
    });
    return () => unsub();
  }, [date]);

  // ── rb_slots is Route Builder's own file — no cross-reading from crews ──

  // ── Profile lookups ──────────────────────────────────────────
  const drivers = profiles.filter(
    (p) => p.role === "driver" && !(p as any).isFormerEmployee,
  );
  const sidemen = profiles.filter(
    (p) => p.role === "sideman" && !(p as any).isFormerEmployee,
  );
  const getP = (id: string | null) =>
    id ? profiles.find((p) => p.id === id) || null : null;

  // Returns true if this profile is in any route slot OR any crewOff entry
  const isAlreadyAssigned = (profileId: string) =>
    slots
      .slice(0, totalSlots)
      .some(
        (s) =>
          s.driverId === profileId ||
          s.sidemanId === profileId ||
          s.sideman2Id === profileId ||
          s.crewOff.some((x) => x.id === profileId) ||
          (s.standby || []).some((x) => x.id === profileId),
      );

  // ── Saved crews for this date (declared early above for effects) ──

  // ── Touch drag support (mobile) ───────────────────────────────
  const touchActiveRef = useRef(false);
  const touchGhostElRef = useRef<HTMLDivElement | null>(null);
  // Stable refs to drop handlers so the global touch useEffect doesn't go stale
  const onDropRef = useRef<typeof onDrop | null>(null);
  const onDropCrewOffRef = useRef<typeof onDropCrewOff | null>(null);
  const onDropStandbyRef = useRef<typeof onDropStandby | null>(null);

  const removeTouchGhost = useCallback(() => {
    if (touchGhostElRef.current) {
      touchGhostElRef.current.remove();
      touchGhostElRef.current = null;
    }
  }, []);

  const handleChipTouchStart = useCallback(
    (e: React.TouchEvent, p: Profile) => {
      e.preventDefault();
      touchActiveRef.current = true;
      dragProfile.current = p;
      dragSource.current = { type: "panel" };
      setDragId(p.id);
      const touch = e.touches[0];
      const ghost = document.createElement("div");
      ghost.style.cssText = `position:fixed;z-index:99999;pointer-events:none;background:#1c1e22;border:1px solid #E85520;border-radius:8px;padding:7px 14px;font-family:'Barlow',sans-serif;font-size:12px;font-weight:700;color:#e8e9eb;white-space:nowrap;transform:translate(-50%,-50%);top:${touch.clientY}px;left:${touch.clientX}px;box-shadow:0 4px 20px rgba(232,85,32,0.5);`;
      ghost.textContent =
        (p as any).username ||
        `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
      document.body.appendChild(ghost);
      touchGhostElRef.current = ghost as HTMLDivElement;
    },
    [],
  );

  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      if (!touchActiveRef.current) return;
      const touch = e.touches[0];
      if (touchGhostElRef.current) {
        touchGhostElRef.current.style.left = touch.clientX + "px";
        touchGhostElRef.current.style.top = touch.clientY + "px";
        touchGhostElRef.current.style.visibility = "hidden";
      }
      const el = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      ) as HTMLElement | null;
      if (touchGhostElRef.current)
        touchGhostElRef.current.style.visibility = "visible";
      const zone = el
        ? (el.closest("[data-drop-key]") as HTMLElement | null)
        : null;
      setOverKey(zone ? zone.dataset.dropKey || null : null);
      e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!touchActiveRef.current) return;
      touchActiveRef.current = false;
      setDragId(null);
      setOverKey(null);
      const touch = e.changedTouches[0];
      if (!touch || !dragProfile.current) {
        removeTouchGhost();
        dragProfile.current = null;
        dragSource.current = null;
        return;
      }
      if (touchGhostElRef.current)
        touchGhostElRef.current.style.visibility = "hidden";
      const el = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      ) as HTMLElement | null;
      removeTouchGhost();
      const zone = el
        ? (el.closest("[data-drop-key]") as HTMLElement | null)
        : null;
      if (!zone) {
        dragProfile.current = null;
        dragSource.current = null;
        return;
      }
      const key = zone.dataset.dropKey || "";
      const fakeEv = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.DragEvent;
      if (key.startsWith("d-")) {
        onDropRef.current?.(fakeEv, parseInt(key.slice(2)), "driver");
      } else if (key.startsWith("s2-")) {
        onDropRef.current?.(fakeEv, parseInt(key.slice(3)), "sideman2");
      } else if (key.startsWith("s-")) {
        onDropRef.current?.(fakeEv, parseInt(key.slice(2)), "sideman");
      } else if (key.startsWith("co-")) {
        onDropCrewOffRef.current?.(fakeEv, parseInt(key.slice(3)));
      } else if (key.startsWith("sb-")) {
        onDropStandbyRef.current?.(fakeEv, parseInt(key.slice(3)));
      }
      dragProfile.current = null;
      dragSource.current = null;
    };
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []); // runs once — uses stable refs below

  const driversAccepted = savedCrews.filter((c) => c.driver.confirmed).length;
  const sidemenAccepted = savedCrews.filter((c) => c.sideman.confirmed).length;
  const driversNotAccepted = savedCrews.filter(
    (c) => !c.driver.confirmed,
  ).length;
  const sidemenNotAccepted = savedCrews.filter(
    (c) => !c.sideman.confirmed,
  ).length;

  // ── Dot status ────────────────────────────────────────────────
  const getDot = (
    slotIdx: number,
    role: "driver" | "sideman" | "sideman2",
  ): DotStatus => {
    const slot = slots[slotIdx];
    const hasId =
      role === "driver"
        ? slot.driverId
        : role === "sideman"
          ? slot.sidemanId
          : slot.sideman2Id;
    if (!hasId) return "empty";
    const crew = savedCrews.find(
      (c) => c.routeNumber === `Route ${slotIdx + 1}`,
    );
    if (!crew) return "pending";
    if (role === "driver")
      return crew.driver.confirmed ? "confirmed" : "pending";
    if (role === "sideman")
      return crew.sideman.confirmed ? "confirmed" : "pending";
    return crew.sideman2?.confirmed ? "confirmed" : "pending";
  };

  // ── Drag handlers ─────────────────────────────────────────────
  const onDragStart = (e: React.DragEvent, p: Profile) => {
    dragProfile.current = p;
    dragSource.current = { type: "panel" };
    setDragId(p.id);
    e.dataTransfer.effectAllowed = "copy";
  };
  const onDragEnd = () => {
    setDragId(null);
    setOverKey(null);
    dragProfile.current = null;
    dragSource.current = null;
  };
  const startTableDrag = (
    e: React.DragEvent,
    p: Profile,
    source: {
      type: "slot" | "crewOff" | "standby";
      slotIdx: number;
      role?: "driver" | "sideman" | "sideman2";
    },
  ) => {
    dragProfile.current = p;
    dragSource.current = source;
    setDragId(p.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const applySourceClear = (
    slotsArr: SlotData[],
    profileId: string,
  ): SlotData[] => {
    const src = dragSource.current;
    if (!src || src.type === "panel") return slotsArr;
    return slotsArr.map((s, i) => {
      if (i !== src.slotIdx) return s;
      if (src.type === "slot" && src.role) {
        const f =
          src.role === "driver"
            ? "driverId"
            : src.role === "sideman"
              ? "sidemanId"
              : "sideman2Id";
        return {
          ...s,
          [f]: (s as any)[f] === profileId ? null : (s as any)[f],
        };
      }
      if (src.type === "crewOff")
        return { ...s, crewOff: s.crewOff.filter((x) => x.id !== profileId) };
      if (src.type === "standby")
        return {
          ...s,
          standby: (s.standby || []).filter((x) => x.id !== profileId),
        };
      return s;
    });
  };
  const onDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault();
    setOverKey(key);
  };
  const onDragLeave = () => setOverKey(null);

  const onDrop = (
    e: React.DragEvent,
    slotIdx: number,
    role: "driver" | "sideman" | "sideman2",
  ) => {
    e.preventDefault();
    setOverKey(null);
    if (isRouteLocked()) {
      setPendingAction(() => () => onDropInner(e, slotIdx, role));
      setLockInput("");
      setShowLockModal(true);
      return;
    }
    onDropInner(e, slotIdx, role);
  };

  const onDropInner = (
    e: React.DragEvent,
    slotIdx: number,
    role: "driver" | "sideman" | "sideman2",
  ) => {
    const p = dragProfile.current;
    if (!p) return;
    const currentField =
      role === "driver"
        ? "driverId"
        : role === "sideman"
          ? "sidemanId"
          : "sideman2Id";
    const movingWithinTable =
      dragSource.current && dragSource.current.type !== "panel";
    const alreadyInThisSlot = slots[slotIdx][currentField] !== p.id;
    const inCrewOff = slots
      .slice(0, totalSlots)
      .some((s) => s.crewOff.some((x) => x.id === p.id));
    if (!movingWithinTable && alreadyInThisSlot && inCrewOff) {
      showToast(
        `${p.firstName} is marked as day off — remove them from Crew Off first.`,
      );
      return;
    }
    if (!movingWithinTable && alreadyInThisSlot && isAlreadyAssigned(p.id)) {
      showToast(`${p.firstName} is already assigned to a route today.`);
      return;
    }
    const effectiveRole = role === "sideman2" ? "sideman" : role;
    if (
      p.role !== effectiveRole &&
      !window.confirm(
        `${p.firstName} is a ${p.role}, not a ${effectiveRole}. Add anyway?`,
      )
    )
      return;
    const field =
      role === "driver"
        ? "driverId"
        : role === "sideman"
          ? "sidemanId"
          : "sideman2Id";
    let next = slots.map((s, i) =>
      i === slotIdx ? { ...s, [field]: p.id } : s,
    );
    next = applySourceClear(next, p.id);
    if (
      dragSource.current?.type === "slot" &&
      dragSource.current.slotIdx !== undefined
    ) {
      const srcIdx = dragSource.current.slotIdx;
      const srcRole = dragSource.current.role!;
      if (srcIdx !== slotIdx || srcRole !== role) {
        const srcRouteNum = routeLabel(slots[srcIdx], srcIdx);
        const liveCrewsNow = useCrewStore.getState().crews;
        const srcCrew = liveCrewsNow.find(
          (c) => c.routeNumber === srcRouteNum && c.workDate === di.shortStr,
        );
        if (srcCrew) {
          if (srcRole === "sideman2") {
            useCrewStore
              .getState()
              .updateCrew(srcCrew.id, (c) => ({ ...c, sideman2: null }));
          } else {
            removeCrew(srcCrew.id);
          }
        }
      }
    }
    setSlots(next);
    saveToFirebase(next);

    const updatedSlot = next[slotIdx];
    const driverId = updatedSlot.driverId;
    const sidemanId = updatedSlot.sidemanId;
    const sideman2Id = updatedSlot.sideman2Id;
    const routeNum = `Route ${slotIdx + 1}`;
    const liveCrews = useCrewStore.getState().crews;
    const existing = liveCrews.find(
      (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
    );

    if (driverId && sidemanId) {
      if (!existing) {
        const driver = getP(driverId);
        const sideman = getP(sidemanId);
        const sideman2Profile = sideman2Id ? getP(sideman2Id) : null;
        if (driver && sideman) {
          const dName =
            (driver as any).username ||
            `${driver.firstName}${driver.lastName ? " " + driver.lastName : ""}`;
          const smName =
            (sideman as any).username ||
            `${sideman.firstName}${sideman.lastName ? " " + sideman.lastName : ""}`;
          const sm2Name = sideman2Profile
            ? (sideman2Profile as any).username ||
              `${sideman2Profile.firstName}${sideman2Profile.lastName ? " " + sideman2Profile.lastName : ""}`
            : undefined;
          const newCrew = makeCrew(
            dName,
            driver.dob || "",
            smName,
            sideman.dob || "",
            date,
            fmt12(updatedSlot.startTime),
            routeNum,
            null,
            sm2Name,
            sideman2Profile?.dob || "",
            driver.id,
            sideman.id,
            sideman2Profile?.id,
          );
          if (updatedSlot.driverNote)
            (newCrew.driver as any).adminNote = updatedSlot.driverNote;
          if (updatedSlot.sidemanNote)
            (newCrew.sideman as any).adminNote = updatedSlot.sidemanNote;
          if (updatedSlot.sideman2Note && newCrew.sideman2)
            (newCrew.sideman2 as any).adminNote = updatedSlot.sideman2Note;
          addCrew(newCrew);
        }
      } else {
        const droppedProfile = getP(p.id);
        if (droppedProfile) {
          const uname =
            (droppedProfile as any).username ||
            `${droppedProfile.firstName}${droppedProfile.lastName ? " " + droppedProfile.lastName : ""}`;
          if (role === "sideman2") {
            useCrewStore.getState().updateCrew(existing.id, (c) => ({
              ...c,
              sideman2: {
                name: uname,
                dob: droppedProfile.dob || "",
                profileId: droppedProfile.id,
                confirmed: false,
                confirmedAt: null,
              },
            }));
          } else {
            useCrewStore.getState().updateCrew(existing.id, (c) => ({
              ...c,
              [role]: {
                ...c[role as "driver" | "sideman"],
                name: uname,
                dob: droppedProfile.dob || "",
                profileId: droppedProfile.id,
              },
            }));
          }
        }
      }
    } else if (role === "sideman2" && sideman2Id && existing) {
      const sm2Profile = getP(sideman2Id);
      if (sm2Profile) {
        const uname =
          (sm2Profile as any).username ||
          `${sm2Profile.firstName}${sm2Profile.lastName ? " " + sm2Profile.lastName : ""}`;
        useCrewStore.getState().updateCrew(existing.id, (c) => ({
          ...c,
          sideman2: {
            name: uname,
            dob: sm2Profile.dob || "",
            profileId: sm2Profile.id,
            confirmed: false,
            confirmedAt: null,
          },
        }));
      }
    }
  };

  const clearSlot = (idx: number, role: "driver" | "sideman" | "sideman2") => {
    const slot = slots[idx];
    const p =
      role === "driver"
        ? getP(slot.driverId)
        : role === "sideman"
          ? getP(slot.sidemanId)
          : getP(slot.sideman2Id);
    const name = p
      ? `${p.firstName}${p.lastName ? " " + p.lastName : ""}`
      : role;
    if (!window.confirm(`Remove ${name} from Route ${idx + 1}?`)) return;
    if (role === "sideman2") {
      const routeNum = routeLabel(slots[idx], idx);
      const liveCrews = useCrewStore.getState().crews;
      const crew = liveCrews.find(
        (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
      );
      if (crew)
        useCrewStore
          .getState()
          .updateCrew(crew.id, (c) => ({ ...c, sideman2: null }));
    } else if (slot.driverId && slot.sidemanId) {
      const routeNum = routeLabel(slots[idx], idx);
      const liveCrews = useCrewStore.getState().crews;
      const crew = liveCrews.find(
        (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
      );
      if (crew) removeCrew(crew.id);
    }
    const idField =
      role === "driver"
        ? "driverId"
        : role === "sideman"
          ? "sidemanId"
          : "sideman2Id";
    const noteField =
      role === "driver"
        ? "driverNote"
        : role === "sideman"
          ? "sidemanNote"
          : "sideman2Note";
    const next = slots.map((s, i) =>
      i === idx ? { ...s, [idField]: null, [noteField]: "" } : s,
    );
    setSlots(next);
    saveToFirebase(next);
  };
  const clearRow = (idx: number) => {
    const slot = slots[idx];
    const d = getP(slot.driverId);
    const s = getP(slot.sidemanId);
    const dName = d
      ? `${d.firstName}${d.lastName ? " " + d.lastName : ""}`
      : "";
    const sName = s
      ? `${s.firstName}${s.lastName ? " " + s.lastName : ""}`
      : "";
    const who =
      [dName, sName].filter(Boolean).join(" & ") || `Route ${idx + 1}`;
    if (
      !window.confirm(
        `Delete Route ${idx + 1} — ${who}? This will remove it from the driver and sideman portal.`,
      )
    )
      return;
    const routeNum = routeLabel(slots[idx], idx);
    const liveCrews = useCrewStore.getState().crews;
    const crew = liveCrews.find(
      (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
    );
    if (crew) removeCrew(crew.id);
    const next = slots.map((s, i) =>
      i === idx
        ? {
            ...s,
            driverId: null,
            sidemanId: null,
            sideman2Id: null,
            driverNote: "",
            sidemanNote: "",
            sideman2Note: "",
          }
        : s,
    );
    setSlots(next);
    saveToFirebase(next);
  };
  const updateTime = (idx: number, time: string) => {
    const next = slots.map((s, i) =>
      i === idx ? { ...s, startTime: time } : s,
    );
    setSlots(next);
    saveToFirebase(next);
    // Read live from store — avoid stale closure
    const routeNum = routeLabel(slots[idx], idx);
    const liveCrews = useCrewStore.getState().crews;
    const crew = liveCrews.find(
      (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
    );
    if (crew) {
      useCrewStore.getState().updateCrew(crew.id, (c) => ({
        ...c,
        startTime: fmt12(time),
      }));
    }
  };
  const updateNote = (
    idx: number,
    role: "driver" | "sideman" | "sideman2",
    note: string,
  ) => {
    const field =
      role === "driver"
        ? "driverNote"
        : role === "sideman"
          ? "sidemanNote"
          : "sideman2Note";
    const next = slots.map((s, i) => (i === idx ? { ...s, [field]: note } : s));
    setSlots(next);
    saveToFirebase(next);
    // Read live from store — avoid stale closure
    const routeNum = routeLabel(slots[idx], idx);
    const liveCrews = useCrewStore.getState().crews;
    const crew = liveCrews.find(
      (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
    );
    if (crew) {
      useCrewStore.getState().updateCrew(crew.id, (c) => {
        if (role === "sideman2" && c.sideman2)
          return { ...c, sideman2: { ...c.sideman2, adminNote: note } };
        if (role === "driver" || role === "sideman")
          return {
            ...c,
            [role]: { ...c[role as "driver" | "sideman"], adminNote: note },
          };
        return c;
      });
    }
  };

  const onDropCrewOff = (e: React.DragEvent, slotIdx: number) => {
    e.preventDefault();
    setOverKey(null);
    if (isRouteLocked()) {
      const p = dragProfile.current;
      const src = dragSource.current;
      setPendingAction(() => () => {
        if (!p) return;
        const movingWithinTable2 = src && src.type !== "panel";
        if (!movingWithinTable2 && isAlreadyAssigned(p.id)) {
          showToast(
            `${p.firstName} is already assigned to a route — cannot mark as day off.`,
          );
          return;
        }
        const label: "D" | "S" = p.role === "driver" ? "D" : "S";
        let next = slots.map((s, i) => {
          if (i !== slotIdx) return s;
          if (s.crewOff.find((x) => x.id === p.id)) return s;
          return { ...s, crewOff: [...s.crewOff, { id: p.id, label }] };
        });
        next = applySourceClear(next, p.id);
        setSlots(next);
        saveToFirebase(next);
      });
      setLockInput("");
      setShowLockModal(true);
      return;
    }
    const p = dragProfile.current;
    if (!p) return;
    const movingWithinTable2 =
      dragSource.current && dragSource.current.type !== "panel";
    if (!movingWithinTable2 && isAlreadyAssigned(p.id)) {
      showToast(
        `${p.firstName} is already assigned to a route — cannot mark as day off.`,
      );
      return;
    }
    const label: "D" | "S" = p.role === "driver" ? "D" : "S";
    let next = slots.map((s, i) => {
      if (i !== slotIdx) return s;
      if (s.crewOff.find((x) => x.id === p.id)) return s;
      return { ...s, crewOff: [...s.crewOff, { id: p.id, label }] };
    });
    next = applySourceClear(next, p.id);
    setSlots(next);
    saveToFirebase(next);
  };

  const removeCrewOff = (slotIdx: number, profileId: string) => {
    const next = slots.map((s, i) =>
      i === slotIdx
        ? { ...s, crewOff: s.crewOff.filter((x) => x.id !== profileId) }
        : s,
    );
    setSlots(next);
    saveToFirebase(next);
    // Also cancel any accepted day-off request for this profile on this date
    onValue(
      ref(db, "dayoff_requests"),
      (snap) => {
        const raw = snap.val();
        if (!raw) return;
        (Object.values(raw) as any[]).forEach((r: any) => {
          if (
            r.profileId === profileId &&
            r.requestDate === date &&
            r.status === "accepted"
          ) {
            fbSet(ref(db, `dayoff_requests/${r.id}/status`), "cancelled");
            fbSet(
              ref(db, `dayoff_requests/${r.id}/reviewedAt`),
              new Date().toISOString(),
            );
          }
        });
      },
      { onlyOnce: true },
    );
  };

  const onDropStandby = (e: React.DragEvent, slotIdx: number) => {
    e.preventDefault();
    setOverKey(null);
    if (isRouteLocked()) {
      const p = dragProfile.current;
      const src = dragSource.current;
      setPendingAction(() => () => {
        if (!p) return;
        const label: "D" | "S" = p.role === "driver" ? "D" : "S";
        let next = slots.map((s, i) => {
          if (i !== slotIdx) return s;
          if ((s.standby || []).find((x) => x.id === p.id)) return s;
          return { ...s, standby: [...(s.standby || []), { id: p.id, label }] };
        });
        next = applySourceClear(next, p.id);
        setSlots(next);
        saveToFirebase(next);
      });
      setLockInput("");
      setShowLockModal(true);
      return;
    }
    const p = dragProfile.current;
    if (!p) return;
    const label: "D" | "S" = p.role === "driver" ? "D" : "S";
    let next = slots.map((s, i) => {
      if (i !== slotIdx) return s;
      if ((s.standby || []).find((x) => x.id === p.id)) return s;
      return { ...s, standby: [...(s.standby || []), { id: p.id, label }] };
    });
    next = applySourceClear(next, p.id);
    setSlots(next);
    saveToFirebase(next);
  };

  const removeStandby = (slotIdx: number, profileId: string) => {
    const next = slots.map((s, i) =>
      i === slotIdx
        ? { ...s, standby: (s.standby || []).filter((x) => x.id !== profileId) }
        : s,
    );
    setSlots(next);
    saveToFirebase(next);
  };

  const resetAll = () => {
    if (!window.confirm(`Clear all ${totalSlots} route slots for this date?`))
      return;
    const empty = emptySlots(50);
    setTotalSlots(50);
    setSlots(empty);
    saveToFirebase(empty);
    useCrewStore
      .getState()
      .crews.filter((c) => c.workDate === di.shortStr)
      .forEach((c) => removeCrew(c.id));
    // Also remove all crew records for this date
    const liveCrews = useCrewStore.getState().crews;
    liveCrews
      .filter((c) => c.workDate === di.shortStr)
      .forEach((c) => removeCrew(c.id));
  };

  const addMoreSlots = () => {
    const next = [
      ...slots,
      {
        driverId: null,
        sidemanId: null,
        startTime: "07:00",
        note: "",
        driverNote: "",
        sidemanNote: "",
        crewOff: [],
        standby: [],
        timeDuration: "",
        sideman2Id: null,
        sideman2Note: "",
      },
    ];
    setTotalSlots(next.length);
    setSlots(next);
    saveToFirebase(next);
  };

  // ── Save routes to crew store ─────────────────────────────────
  const saveRoutes = () => {
    const filled = slots
      .map((s, i) => ({ ...s, idx: i }))
      .filter((s) => s.driverId && s.sidemanId);
    if (!filled.length) {
      showToast("Drop driver + sideman into slots first");
      return;
    }
    let saved = 0;
    filled.forEach((s) => {
      const routeNum = routeLabel(s, s.idx);
      const liveCrews = useCrewStore.getState().crews;
      const existing = liveCrews.find(
        (c) => c.routeNumber === routeNum && c.workDate === di.shortStr,
      );
      const driver = getP(s.driverId);
      const sideman = getP(s.sidemanId);
      if (!driver || !sideman) return;
      const dName =
        (driver as any).username ||
        `${driver.firstName}${driver.lastName ? " " + driver.lastName : ""}`;
      const smName =
        (sideman as any).username ||
        `${sideman.firstName}${sideman.lastName ? " " + sideman.lastName : ""}`;
      if (!existing) {
        // Create new crew record with notes included
        const newCrew = makeCrew(
          dName,
          driver.dob || "",
          smName,
          sideman.dob || "",
          date,
          fmt12(s.startTime),
          routeNum,
          null,
          undefined,
          undefined,
          driver.id,
          sideman.id,
        );
        if (s.driverNote) (newCrew.driver as any).adminNote = s.driverNote;
        if (s.sidemanNote) (newCrew.sideman as any).adminNote = s.sidemanNote;
        addCrew(newCrew);
        saved++;
      } else {
        const sm2Profile = s.sideman2Id ? getP(s.sideman2Id) : null;
        useCrewStore.getState().updateCrew(existing.id, (c) => ({
          ...c,
          startTime: fmt12(s.startTime),
          driver: {
            ...c.driver,
            name: dName,
            dob: driver.dob || "",
            profileId: driver.id,
            adminNote: s.driverNote || (c.driver as any).adminNote || "",
          },
          sideman: {
            ...c.sideman,
            name: smName,
            dob: sideman.dob || "",
            profileId: sideman.id,
            adminNote: s.sidemanNote || (c.sideman as any).adminNote || "",
          },
          sideman2: sm2Profile
            ? {
                name: `${sm2Profile.firstName}${sm2Profile.lastName ? " " + sm2Profile.lastName : ""}`,
                dob: sm2Profile.dob || "",
                confirmed: c.sideman2?.confirmed || false,
                confirmedAt: c.sideman2?.confirmedAt || null,
                adminNote:
                  s.sideman2Note || (c.sideman2 as any)?.adminNote || "",
              }
            : c.sideman2,
        }));
      }
    });
    setViewingDate(new Date(date + "T00:00:00"));
    showToast(
      saved > 0
        ? `${saved} route${saved !== 1 ? "s" : ""} saved!`
        : "All routes synced to crew portal.",
    );
  };

  const activeSlots = slots.slice(0, totalSlots);
  const filledCount = activeSlots.filter(
    (s) => s.driverId && s.sidemanId,
  ).length;
  const partialCount = activeSlots.filter(
    (s) => (s.driverId || s.sidemanId) && !(s.driverId && s.sidemanId),
  ).length;

  const pLbl: React.CSSProperties = {
    fontSize: 9,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    color: "#7a7d85",
    display: "flex",
    alignItems: "center",
    gap: 5,
  };

  // Keep touch handler refs up-to-date every render
  onDropRef.current = onDrop;
  onDropCrewOffRef.current = onDropCrewOff;
  onDropStandbyRef.current = onDropStandby;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        fontFamily: "'Barlow',sans-serif",
      }}
    >
      {/* ── TOP BAR — no Work Date input, date comes from header arrows ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          background: "#141518",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          flexShrink: 0,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 20,
              fontWeight: 800,
              textTransform: "uppercase",
              color: "#e8e9eb",
            }}
          >
            Routes
          </span>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[
              {
                l: `${filledCount} ready`,
                bg: "rgba(62,207,110,0.1)",
                bc: "rgba(62,207,110,0.3)",
                c: "#3ecf6e",
              },
              ...(partialCount > 0
                ? [
                    {
                      l: `${partialCount} partial`,
                      bg: "rgba(232,85,32,0.1)",
                      bc: "rgba(232,85,32,0.3)",
                      c: "#E85520",
                    },
                  ]
                : []),
              {
                l: `${totalSlots - filledCount - partialCount} empty`,
                bg: "rgba(255,255,255,0.03)",
                bc: "rgba(255,255,255,0.08)",
                c: "#7a7d85",
              },
            ].map((s, i) => (
              <span
                key={i}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 10,
                  border: `1px solid ${s.bc}`,
                  background: s.bg,
                  color: s.c,
                }}
              >
                {s.l}
              </span>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => {
              if (
                !window.confirm(
                  "DELETE ALL crew records across ALL dates and rebuild from current slots only? This fixes all ghost routes everywhere.",
                )
              )
                return;
              const liveCrews = useCrewStore.getState().crews;
              liveCrews.forEach((c) => removeCrew(c.id));
              showToast(
                "Deleted ${liveCrews.length} crew records. Now go to each date and hit Save Routes.",
              );
            }}
            style={{
              padding: "7px 12px",
              background: "transparent",
              border: "1px solid rgba(255,0,0,0.5)",
              borderRadius: 7,
              color: "#ff4444",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
            }}
          >
            Nuke All
          </button>
          <button
            onClick={resetAll}
            style={{
              padding: "7px 12px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 7,
              color: "#7a7d85",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
            }}
          >
            Reset
          </button>
          {/* Download route template */}
          <button
            onClick={() => {
              const csv = [
                [
                  "Route No (required)",
                  "Driver Username (required)",
                  "Sideman Username (required)",
                  "Sideman 2 Username (optional)",
                  "Crew Off Usernames (optional, comma-separated)",
                  "Standby Usernames (optional, comma-separated)",
                  "Start Time HH:MM (optional)",
                ],
                [
                  "1",
                  "kirpal_driver",
                  "john_sideman",
                  "prince_sideman",
                  "",
                  "07:00",
                ],
                ["2", "gurjeet_driver", "jane_sideman", "", "", "08:30"],
                ["3", "", "", "", "kirpal_driver,john_sideman,"],
              ]
                .map((r) =>
                  r.map((c) => '"' + c.replace(/"/g, '""') + '"').join(","),
                )
                .join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "FleetOps_Route_Template.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{
              padding: "7px 12px",
              background: "transparent",
              border: "1px solid rgba(62,207,110,0.4)",
              borderRadius: 7,
              color: "#3ecf6e",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
            }}
          >
            ↓ Template
          </button>
          {/* Upload routes CSV */}
          <label style={{ cursor: "pointer" }}>
            <span
              style={{
                padding: "7px 12px",
                background: "transparent",
                border: "1px solid rgba(100,160,255,0.4)",
                borderRadius: 7,
                color: "#64a0ff",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: "'Barlow',sans-serif",
                display: "inline-block",
              }}
            >
              ↑ Upload Routes
            </span>
            <input
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  withLockCheck(() => {
                    const text = ev.target?.result as string;
                    const lines = text
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean);
                    if (lines.length < 2) {
                      showToast("No data rows found");
                      return;
                    }
                    const parseCSVLine = (line: string): string[] => {
                      const cols: string[] = [];
                      let cur = "";
                      let inQ = false;
                      for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (ch === '"') {
                          inQ = !inQ;
                        } else if (ch === "," && !inQ) {
                          cols.push(cur.replace(/^"|"$/g, "").trim());
                          cur = "";
                        } else {
                          cur += ch;
                        }
                      }
                      cols.push(cur.replace(/^"|"$/g, "").trim());
                      return cols;
                    };
                    const headers = parseCSVLine(lines[0]).map((h) =>
                      h.toLowerCase(),
                    );
                    const col = (row: string[], name: string) => {
                      const idx = headers.findIndex((h) => h.includes(name));
                      return idx >= 0 ? (row[idx] || "").trim() : "";
                    };
                    const findByUsername = (uname: string) =>
                      profiles.find(
                        (p) =>
                          ((p as any).username || "").toLowerCase() ===
                          uname.toLowerCase(),
                      ) || null;
                    let updated = 0;
                    const newSlots = [...slots];
                    lines.slice(1).forEach((line) => {
                      const cols = parseCSVLine(line);
                      const routeNoStr = col(cols, "route no");
                      const driverUn = col(cols, "driver");
                      const sidemanUn = col(cols, "sideman username");
                      const sideman2Un = col(cols, "sideman 2");
                      const crewOffRaw = col(cols, "crew off");
                      const standbyRaw = col(cols, "standby");
                      const timeDurationRaw = col(cols, "time duration");
                      const startTimeIdx = headers.findIndex((h) =>
                        h.includes("start time"),
                      );
                      const startTimeRaw =
                        startTimeIdx >= 0
                          ? (cols[startTimeIdx] || "")
                              .replace(/^"|"$/g, "")
                              .trim()
                          : "";
                      const routeNo = parseInt(routeNoStr, 10);
                      if (!routeNoStr || isNaN(routeNo) || routeNo < 1) return;
                      const slotIdx = routeNo - 1;
                      while (newSlots.length <= slotIdx) {
                        newSlots.push({
                          driverId: null,
                          sidemanId: null,
                          startTime: "07:00",
                          note: "",
                          driverNote: "",
                          sidemanNote: "",
                          crewOff: [],
                          standby: [],
                          timeDuration: "",
                          sideman2Id: null,
                          sideman2Note: "",
                        });
                      }
                      const driverP = driverUn
                        ? findByUsername(driverUn)
                        : null;
                      const sidemanP = sidemanUn
                        ? findByUsername(sidemanUn)
                        : null;
                      const sideman2P = sideman2Un
                        ? findByUsername(sideman2Un)
                        : null;
                      const crewOffEntries: Array<{
                        id: string;
                        label: "D" | "S";
                      }> = crewOffRaw
                        ? (crewOffRaw
                            .split(",")
                            .map((u) => u.trim())
                            .filter(Boolean)
                            .map((u) => {
                              const p = findByUsername(u);
                              if (!p) return null;
                              return {
                                id: p.id,
                                label:
                                  p.role === "driver"
                                    ? ("D" as const)
                                    : ("S" as const),
                              };
                            })
                            .filter(Boolean) as Array<{
                            id: string;
                            label: "D" | "S";
                          }>)
                        : [];
                      newSlots[slotIdx] = {
                        ...newSlots[slotIdx],
                        driverId: driverP
                          ? driverP.id
                          : newSlots[slotIdx].driverId,
                        sidemanId: sidemanP
                          ? sidemanP.id
                          : newSlots[slotIdx].sidemanId,
                        sideman2Id: sideman2P
                          ? sideman2P.id
                          : newSlots[slotIdx].sideman2Id,
                        crewOff:
                          crewOffEntries.length > 0
                            ? crewOffEntries
                            : newSlots[slotIdx].crewOff,
                        standby: standbyRaw
                          ? (standbyRaw
                              .split(",")
                              .map((u) => u.trim())
                              .filter(Boolean)
                              .map((u) => {
                                const p = findByUsername(u);
                                if (!p) return null;
                                return {
                                  id: p.id,
                                  label:
                                    p.role === "driver"
                                      ? ("D" as const)
                                      : ("S" as const),
                                };
                              })
                              .filter(Boolean) as Array<{
                              id: string;
                              label: "D" | "S";
                            }>)
                          : newSlots[slotIdx].standby || [],
                        startTime:
                          startTimeRaw && /^\d{1,2}:\d{2}$/.test(startTimeRaw)
                            ? startTimeRaw.padStart(5, "0")
                            : newSlots[slotIdx].startTime,
                        timeDuration:
                          timeDurationRaw || newSlots[slotIdx].timeDuration,
                      };
                      updated++;
                    });
                    setTotalSlots(Math.max(50, newSlots.length));
                    setSlots(newSlots);
                    saveToFirebase(newSlots);

                    // Auto-create crew records for all filled slots
                    let crewSaved = 0;
                    newSlots.forEach((s, slotIdx) => {
                      if (!s.driverId || !s.sidemanId) return;
                      const routeNum = routeLabel(slots[slotIdx], slotIdx);
                      const liveCrews = useCrewStore.getState().crews;
                      const existing = liveCrews.find(
                        (c) =>
                          c.routeNumber === routeNum &&
                          c.workDate === di.shortStr,
                      );
                      const driver = profiles.find((p) => p.id === s.driverId);
                      const sideman = profiles.find(
                        (p) => p.id === s.sidemanId,
                      );
                      const sm2 = s.sideman2Id
                        ? profiles.find((p) => p.id === s.sideman2Id)
                        : null;
                      if (!driver || !sideman) return;
                      const dName =
                        (driver as any).username ||
                        `${driver.firstName}${driver.lastName ? " " + driver.lastName : ""}`;
                      const smName =
                        (sideman as any).username ||
                        `${sideman.firstName}${sideman.lastName ? " " + sideman.lastName : ""}`;
                      const sm2Name = sm2
                        ? (sm2 as any).username ||
                          `${sm2.firstName}${sm2.lastName ? " " + sm2.lastName : ""}`
                        : undefined;
                      if (!existing) {
                        const newCrew = makeCrew(
                          dName,
                          driver.dob || "",
                          smName,
                          sideman.dob || "",
                          date,
                          fmt12(s.startTime),
                          routeNum,
                          null,
                          sm2Name,
                          sm2?.dob || "",
                          driver.id,
                          sideman.id,
                          sm2?.id,
                        );
                        if (s.driverNote)
                          (newCrew.driver as any).adminNote = s.driverNote;
                        if (s.sidemanNote)
                          (newCrew.sideman as any).adminNote = s.sidemanNote;
                        addCrew(newCrew);
                        crewSaved++;
                      } else {
                        useCrewStore
                          .getState()
                          .updateCrew(existing.id, (c) => ({
                            ...c,
                            startTime: fmt12(s.startTime),
                            driver: {
                              ...c.driver,
                              name: dName,
                              dob: driver.dob || "",
                            },
                            sideman: {
                              ...c.sideman,
                              name: smName,
                              dob: sideman.dob || "",
                            },
                            sideman2: sm2
                              ? {
                                  name: sm2Name!,
                                  dob: sm2.dob || "",
                                  confirmed: c.sideman2?.confirmed || false,
                                  confirmedAt: c.sideman2?.confirmedAt || null,
                                }
                              : c.sideman2,
                          }));
                      }
                    });

                    showToast(
                      `${updated} route${updated !== 1 ? "s" : ""} scheduled — ${crewSaved} crew record${crewSaved !== 1 ? "s" : ""} created!`,
                    );
                  }); // end withLockCheck
                };
                reader.readAsText(file);
                e.target.value = "";
              }}
            />
          </label>
          <button
            onClick={() => {
              const rows = [
                [
                  "Route No",
                  "Driver Username",
                  "Sideman Username",
                  "Sideman 2 Username",
                  "Start Time",
                  "Time Duration",
                  "Crew Off Usernames",
                  "Standby Usernames",
                ],
              ];
              slots.slice(0, totalSlots).forEach((s, i) => {
                const d = getP(s.driverId);
                const sm = getP(s.sidemanId);
                const sm2 = getP(s.sideman2Id);
                if (!d && !sm) return;
                const crewOffNames = s.crewOff
                  .map((x) => {
                    const p = getP(x.id);
                    return p
                      ? (p as any).username ||
                          `${p.firstName}${p.lastName ? " " + p.lastName : ""}`
                      : "";
                  })
                  .filter(Boolean)
                  .join(",");
                rows.push([
                  String(i + 1),
                  d
                    ? (d as any).username ||
                      `${d.firstName}${d.lastName ? " " + d.lastName : ""}`
                    : "",
                  sm
                    ? (sm as any).username ||
                      `${sm.firstName}${sm.lastName ? " " + sm.lastName : ""}`
                    : "",
                  sm2
                    ? (sm2 as any).username ||
                      `${sm2.firstName}${sm2.lastName ? " " + sm2.lastName : ""}`
                    : "",
                  s.startTime || "",
                  s.timeDuration || "",
                  crewOffNames,
                  (s.standby || [])
                    .map((x) => {
                      const p = getP(x.id);
                      return p
                        ? (p as any).username ||
                            `${p.firstName}${p.lastName ? " " + p.lastName : ""}`
                        : "";
                    })
                    .filter(Boolean)
                    .join(","),
                ]);
              });
              const csv = rows
                .map((r) =>
                  r
                    .map((c) => '"' + String(c).replace(/"/g, '""') + '"')
                    .join(","),
                )
                .join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `FleetOps_Routes_${date}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{
              padding: "7px 12px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 7,
              color: "#7a7d85",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
            }}
          >
            ↓ Export
          </button>
          <button
            onClick={() => withLockCheck(saveRoutes)}
            style={{
              padding: "8px 18px",
              background: "#E85520",
              border: "none",
              borderRadius: 7,
              color: "#000",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
            }}
          >
            Save Routes {filledCount > 0 ? `(${filledCount})` : ""}
          </button>
        </div>
      </div>

      {/* ── SEARCH + DATE JUMP BAR ── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "8px 16px",
          background: "#0d0e10",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
          alignItems: "center",
        }}
      >
        <input
          type="text"
          value={routeSearch}
          onChange={(e) => setRouteSearch(e.target.value)}
          placeholder="Search routes by username…"
          autoComplete="off"
          style={{
            flex: 1,
            background: "#1c1e22",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "7px 12px",
            color: "#e8e9eb",
            fontFamily: "'Barlow',sans-serif",
            fontSize: 12,
            outline: "none",
          }}
        />
        <input
          type="date"
          value={date}
          onChange={(e) => {
            if (e.target.value)
              setViewingDate(new Date(e.target.value + "T00:00:00"));
          }}
          style={{
            background: "#1c1e22",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "7px 10px",
            color: "#e8e9eb",
            fontFamily: "'Barlow',sans-serif",
            fontSize: 12,
            outline: "none",
            cursor: "pointer",
          }}
        />
      </div>

      {/* ── STATUS BAR ── */}
      <div
        style={{
          padding: "10px 18px",
          background: "#0a0b0d",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          gap: 24,
          flexWrap: "wrap",
          flexShrink: 0,
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#454850",
            whiteSpace: "nowrap",
          }}
        >
          Live Status ({di.shortStr}):
        </span>
        {[
          {
            key: "driversAccepted" as const,
            label: `✓ Drivers Accepted: ${driversAccepted}`,
            color: "#3ecf6e",
          },
          {
            key: "sidemenAccepted" as const,
            label: `✓ Sidemen Accepted: ${sidemenAccepted}`,
            color: "#3ecf6e",
          },
          {
            key: "driversPending" as const,
            label: `✗ Drivers Pending: ${driversNotAccepted}`,
            color: "#e05252",
          },
          {
            key: "sidemenPending" as const,
            label: `✗ Sidemen Pending: ${sidemenNotAccepted}`,
            color: "#e05252",
          },
        ].map((item) => (
          <span
            key={item.key}
            onClick={() => setStatusOverlay(item.key)}
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: item.color,
              whiteSpace: "nowrap",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            {item.label}
          </span>
        ))}
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#7a7d85",
            marginLeft: "auto",
            whiteSpace: "nowrap",
          }}
        >
          Total Saved: {savedCrews.length}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* ── PROFILES PANEL ── */}
        <div
          style={{
            width: 196,
            flexShrink: 0,
            background: "#141518",
            borderRight: "1px solid rgba(255,255,255,0.07)",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "12px 10px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {/* Drivers */}
          <div>
            <button
              onClick={() => setDriversOpen((o) => !o)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0 0 6px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                marginBottom: 6,
              }}
            >
              <span style={pLbl as React.CSSProperties}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="8"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M4 20c0-4 3.6-7 8-7s8 3 8 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Drivers (
                {drivers.filter((p) => !isAlreadyAssigned(p.id)).length}/
                {drivers.length})
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                style={{
                  color: "#454850",
                  transform: driversOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {driversOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {drivers.length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#454850",
                      fontStyle: "italic",
                      padding: "4px 0",
                    }}
                  >
                    No driver profiles
                  </div>
                ) : (
                  drivers
                    .filter((p) => !isAlreadyAssigned(p.id))
                    .map((p) => (
                      <div
                        key={p.id}
                        style={{ touchAction: "none" }}
                        onTouchStart={(e) => handleChipTouchStart(e, p)}
                      >
                        <Chip
                          p={p}
                          dragging={dragId === p.id}
                          assigned={false}
                          onStart={onDragStart}
                          onEnd={onDragEnd}
                        />
                      </div>
                    ))
                )}
              </div>
            )}
          </div>

          {/* Sidemen */}
          <div>
            <button
              onClick={() => setSidemenOpen((o) => !o)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0 0 6px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                marginBottom: 6,
              }}
            >
              <span style={pLbl as React.CSSProperties}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="8"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M4 20c0-4 3.6-7 8-7s8 3 8 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Sidemen (
                {sidemen.filter((p) => !isAlreadyAssigned(p.id)).length}/
                {sidemen.length})
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                style={{
                  color: "#454850",
                  transform: sidemenOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {sidemenOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {sidemen.length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#454850",
                      fontStyle: "italic",
                      padding: "4px 0",
                    }}
                  >
                    No sideman profiles
                  </div>
                ) : (
                  sidemen
                    .filter((p) => !isAlreadyAssigned(p.id))
                    .map((p) => (
                      <div
                        key={p.id}
                        style={{ touchAction: "none" }}
                        onTouchStart={(e) => handleChipTouchStart(e, p)}
                      >
                        <Chip
                          p={p}
                          dragging={dragId === p.id}
                          assigned={false}
                          onStart={onDragStart}
                          onEnd={onDragEnd}
                        />
                      </div>
                    ))
                )}
              </div>
            )}
          </div>

          <div
            style={{
              fontSize: 10,
              color: "#454850",
              lineHeight: 1.5,
              marginTop: "auto",
              paddingTop: 8,
              borderTop: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            Slots auto-save to Firebase — persist across devices &amp;
            refreshes.
          </div>
        </div>

        {/* ── SLOTS TABLE ── */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
          {/* Table header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "64px 180px 180px 180px 90px 90px 32px 32px 32px 32px 1fr 1fr",
              minWidth: 1140,
              background: "#0d0e10",
              borderBottom: "2px solid rgba(255,255,255,0.08)",
              position: "sticky",
              top: 0,
              zIndex: 5,
            }}
          >
            {[
              "Route",
              "Driver",
              "Sideman",
              "Sideman 2",
              "Start",
              "Time Duration",
              "",
              "Notes",
              "",
              "",
              "CREW OFF",
              "STANDBY",
            ].map((h, i) => (
              <div
                key={i}
                style={{
                  padding: "9px 10px",
                  fontSize: 9,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.7px",
                  color: "#454850",
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {/* Loading state */}
          {!loaded && (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: "#454850",
                fontSize: 13,
              }}
            >
              Loading slots…
            </div>
          )}

          {/* Rows */}
          {loaded &&
            slots.slice(0, totalSlots).map((slot, idx) => {
              const dP = getP(slot.driverId);
              const sP = getP(slot.sidemanId);
              const s2P = getP(slot.sideman2Id);
              if (routeSearch.trim()) {
                const q = routeSearch.toLowerCase();
                const matchP = (p: typeof dP) =>
                  p
                    ? ((p as any).username || "").toLowerCase().includes(q) ||
                      `${p.firstName} ${p.lastName || ""}`
                        .toLowerCase()
                        .includes(q)
                    : false;
                const routeNoMatch = String(idx + 1).includes(q);
                if (!matchP(dP) && !matchP(sP) && !matchP(s2P) && !routeNoMatch)
                  return null;
              }
              const routeNum = routeLabel(slot, idx);
              const driverP = dP;
              const sidemanP = sP;
              const dKey = "d-" + idx;
              const sKey = "s-" + idx;
              const dDot = getDot(idx, "driver");
              const sDot = getDot(idx, "sideman");
              const isFilled = !!(slot.driverId && slot.sidemanId);
              const isPartial = !!(
                (slot.driverId || slot.sidemanId) &&
                !isFilled
              );

              return (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "64px 180px 180px 180px 90px 90px 32px 32px 32px 32px 1fr 1fr",
                    minWidth: 1140,
                    minHeight: 28,
                    alignItems: "center",
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                    background: isFilled
                      ? "rgba(62,207,110,0.025)"
                      : isPartial
                        ? "rgba(232,85,32,0.02)"
                        : "transparent",
                  }}
                >
                  {/* Route number */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px 8px",
                    }}
                  >
                    <input
                      value={slot.routeLabel ?? String(idx + 1)}
                      onChange={(e) => {
                        const next = slots.map((s, i) =>
                          i === idx ? { ...s, routeLabel: e.target.value } : s,
                        );
                        setSlots(next);
                        saveToFirebase(next);
                      }}
                      title="Edit route number"
                      style={{
                        width: 44,
                        height: 34,
                        borderRadius: 9,
                        textAlign: "center",
                        fontFamily: "'Barlow Condensed',sans-serif",
                        fontSize: 13,
                        fontWeight: 800,
                        flexShrink: 0,
                        background: isFilled
                          ? "rgba(62,207,110,0.1)"
                          : isPartial
                            ? "rgba(232,85,32,0.07)"
                            : "#1c1e22",
                        border: `1px solid ${isFilled ? "rgba(62,207,110,0.3)" : isPartial ? "rgba(232,85,32,0.2)" : "rgba(255,255,255,0.07)"}`,
                        color: isFilled
                          ? "#3ecf6e"
                          : isPartial
                            ? "#E85520"
                            : "#7a7d85",
                        outline: "none",
                        cursor: "text",
                        padding: 0,
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = "#E85520";
                        e.currentTarget.style.boxShadow =
                          "0 0 0 2px rgba(232,85,32,0.2)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = isFilled
                          ? "rgba(62,207,110,0.3)"
                          : isPartial
                            ? "rgba(232,85,32,0.2)"
                            : "rgba(255,255,255,0.07)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  <div data-drop-key={dKey} style={{ display: "contents" }}>
                    <DropZone
                      profile={driverP}
                      isOver={overKey === dKey}
                      dot={dDot}
                      label="Drop driver"
                      onDragOver={(e) => onDragOver(e, dKey)}
                      onDragLeave={onDragLeave}
                      onDrop={(e) => onDrop(e, idx, "driver")}
                      onClear={() =>
                        withLockCheck(() => clearSlot(idx, "driver"))
                      }
                      onNameClick={
                        driverP
                          ? () => setOverlayProfileId(driverP.id)
                          : undefined
                      }
                      onTableDragStart={
                        driverP
                          ? (e) =>
                              startTableDrag(e, driverP, {
                                type: "slot",
                                slotIdx: idx,
                                role: "driver",
                              })
                          : undefined
                      }
                    />
                  </div>

                  <div data-drop-key={sKey} style={{ display: "contents" }}>
                    <DropZone
                      profile={sidemanP}
                      isOver={overKey === sKey}
                      dot={sDot}
                      label="Drop sideman"
                      onDragOver={(e) => onDragOver(e, sKey)}
                      onDragLeave={onDragLeave}
                      onDrop={(e) => onDrop(e, idx, "sideman")}
                      onClear={() =>
                        withLockCheck(() => clearSlot(idx, "sideman"))
                      }
                      onNameClick={
                        sidemanP
                          ? () => setOverlayProfileId(sidemanP.id)
                          : undefined
                      }
                      onTableDragStart={
                        sidemanP
                          ? (e) =>
                              startTableDrag(e, sidemanP, {
                                type: "slot",
                                slotIdx: idx,
                                role: "sideman",
                              })
                          : undefined
                      }
                    />
                  </div>

                  {/* Sideman 2 */}
                  {(overKey === "s2-" + idx || true) && (
                    <div
                      data-drop-key={"s2-" + idx}
                      style={{ display: "contents" }}
                    >
                      <DropZone
                        profile={getP(slot.sideman2Id)}
                        isOver={overKey === "s2-" + idx}
                        dot={getDot(idx, "sideman2")}
                        label="Drop sideman 2"
                        onDragOver={(e) => onDragOver(e, "s2-" + idx)}
                        onDragLeave={onDragLeave}
                        onDrop={(e) => onDrop(e, idx, "sideman2")}
                        onClear={() =>
                          withLockCheck(() => clearSlot(idx, "sideman2"))
                        }
                        onNameClick={
                          getP(slot.sideman2Id)
                            ? () =>
                                setOverlayProfileId(getP(slot.sideman2Id)!.id)
                            : undefined
                        }
                        onTableDragStart={
                          getP(slot.sideman2Id)
                            ? (e) =>
                                startTableDrag(e, getP(slot.sideman2Id)!, {
                                  type: "slot",
                                  slotIdx: idx,
                                  role: "sideman2",
                                })
                            : undefined
                        }
                      />
                    </div>
                  )}

                  {/* Time picker */}
                  <StartTimePicker
                    value={slot.startTime}
                    onChange={(v) => updateTime(idx, v)}
                  />

                  {/* Time Duration */}
                  <div style={{ padding: "4px 6px" }}>
                    <input
                      type="text"
                      value={slot.timeDuration}
                      onChange={(e) => {
                        let raw = e.target.value.replace(/[^0-9]/g, "");
                        if (raw.length > 2)
                          raw = raw.slice(0, 2) + ":" + raw.slice(2, 4);
                        const next = slots.map((s, i) =>
                          i === idx ? { ...s, timeDuration: raw } : s,
                        );
                        setSlots(next);
                        saveToFirebase(next);
                      }}
                      placeholder="HH:MM"
                      maxLength={5}
                      style={{
                        width: "100%",
                        background: "transparent",
                        border: "1px solid transparent",
                        borderRadius: 5,
                        padding: "6px 4px",
                        color: "#7a7d85",
                        fontFamily: "'Barlow',sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                        outline: "none",
                        cursor: "text",
                        transition: "all 0.15s",
                        textAlign: "center",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.background = "#1c1e22";
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.12)";
                        e.currentTarget.style.color = "#e8e9eb";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = "transparent";
                        e.currentTarget.style.color = "#7a7d85";
                      }}
                    />
                  </div>

                  {/* Clear row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {(slot.driverId || slot.sidemanId) && (
                      <button
                        onClick={() => withLockCheck(() => clearRow(idx))}
                        title="Clear row"
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 5,
                          background: "none",
                          border: "none",
                          color: "#454850",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "color 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#e05252")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "#454850")
                        }
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M18 6L6 18M6 6l12 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Driver note icon */}
                  <NoteCell
                    note={slot.driverNote || ""}
                    label="Driver Note"
                    onChange={(note) => updateNote(idx, "driver", note)}
                  />

                  {/* Sideman note icon */}
                  <NoteCell
                    note={slot.sidemanNote || ""}
                    label="Sideman Note"
                    onChange={(note) => updateNote(idx, "sideman", note)}
                  />

                  {/* Sideman 2 note icon */}
                  <NoteCell
                    note={slot.sideman2Note || ""}
                    label="Sideman 2 Note"
                    onChange={(note) => updateNote(idx, "sideman2", note)}
                  />

                  {/* Crew Off drop zone */}
                  <div
                    data-drop-key={"co-" + idx}
                    style={{
                      padding: "3px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                      minHeight: 40,
                      borderRadius: 7,
                      border: `1px dashed ${overKey === "co-" + idx ? "#E85520" : "rgba(255,255,255,0.07)"}`,
                      background:
                        overKey === "co-" + idx
                          ? "rgba(232,85,32,0.07)"
                          : "transparent",
                      transition: "all 0.15s",
                      margin: "3px 5px",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setOverKey("co-" + idx);
                    }}
                    onDragLeave={() => setOverKey(null)}
                    onDrop={(e) => onDropCrewOff(e, idx)}
                  >
                    {slot.crewOff.length === 0 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#454850",
                          fontWeight: 500,
                          pointerEvents: "none",
                        }}
                      >
                        + Day off
                      </span>
                    )}
                    {slot.crewOff.map((entry) => {
                      const p = getP(entry.id);
                      if (!p) return null;
                      return (
                        <div
                          key={entry.id}
                          draggable
                          onDragStart={(e) =>
                            startTableDrag(e, p, {
                              type: "crewOff",
                              slotIdx: idx,
                            })
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            background: "#1c1e22",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 20,
                            padding: "3px 8px 3px 4px",
                            cursor: "grab",
                          }}
                        >
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              background:
                                entry.label === "D"
                                  ? "rgba(62,207,110,0.15)"
                                  : "rgba(100,160,255,0.15)",
                              border: `2px solid ${entry.label === "D" ? "#3ecf6e" : "#64a0ff"}`,
                              boxShadow:
                                entry.label === "D"
                                  ? "0 0 7px rgba(62,207,110,0.8)"
                                  : "0 0 7px rgba(100,160,255,0.8)",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color:
                                entry.label === "D" ? "#3ecf6e" : "#64a0ff",
                            }}
                          >
                            {entry.label}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#e8e9eb",
                              maxWidth: 80,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {(p as any).username ||
                              `${p.firstName}${p.lastName ? " " + p.lastName : ""}`}
                          </span>
                          <button
                            onClick={() =>
                              withLockCheck(() => removeCrewOff(idx, entry.id))
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: "#454850",
                              cursor: "pointer",
                              fontSize: 14,
                              padding: "0 1px",
                              lineHeight: 1,
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.color = "#e05252")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.color = "#454850")
                            }
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Standby drop zone */}
                  <div
                    data-drop-key={"sb-" + idx}
                    style={{
                      padding: "3px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                      minHeight: 40,
                      borderRadius: 7,
                      border: `1px dashed ${overKey === "sb-" + idx ? "#64a0ff" : "rgba(255,255,255,0.07)"}`,
                      background:
                        overKey === "sb-" + idx
                          ? "rgba(100,160,255,0.07)"
                          : "transparent",
                      transition: "all 0.15s",
                      margin: "3px 5px",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setOverKey("sb-" + idx);
                    }}
                    onDragLeave={() => setOverKey(null)}
                    onDrop={(e) => onDropStandby(e, idx)}
                  >
                    {(slot.standby || []).length === 0 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#454850",
                          fontWeight: 500,
                          pointerEvents: "none",
                        }}
                      >
                        + Standby
                      </span>
                    )}
                    {(slot.standby || []).map((entry) => {
                      const p = getP(entry.id);
                      if (!p) return null;
                      return (
                        <div
                          key={entry.id}
                          draggable
                          onDragStart={(e) =>
                            startTableDrag(e, p, {
                              type: "standby",
                              slotIdx: idx,
                            })
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            background: "#1c1e22",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 20,
                            padding: "3px 8px 3px 4px",
                            cursor: "grab",
                          }}
                        >
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              background: "rgba(100,160,255,0.15)",
                              border: "2px solid #64a0ff",
                              boxShadow: "0 0 7px rgba(100,160,255,0.8)",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: "#64a0ff",
                            }}
                          >
                            {entry.label}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#e8e9eb",
                              maxWidth: 80,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {(p as any).username ||
                              `${p.firstName}${p.lastName ? " " + p.lastName : ""}`}
                          </span>
                          <button
                            onClick={() =>
                              withLockCheck(() => removeStandby(idx, entry.id))
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: "#454850",
                              cursor: "pointer",
                              fontSize: 14,
                              padding: "0 1px",
                              lineHeight: 1,
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.color = "#e05252")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.color = "#454850")
                            }
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          {/* ── ADD MORE TEMPLATES ── */}
          <div
            style={{
              padding: "12px 8px 40px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <button
              onClick={addMoreSlots}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "11px 28px",
                background: "transparent",
                border: "1px dashed rgba(232,85,32,0.4)",
                borderRadius: 9,
                color: "#E85520",
                fontFamily: "'Barlow',sans-serif",
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(232,85,32,0.08)";
                e.currentTarget.style.borderColor = "rgba(232,85,32,0.7)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(232,85,32,0.4)";
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
              Add Template ({totalSlots + 1})
            </button>
          </div>
        </div>

        {/* ── LOCK OVERLAY — covers only the profiles panel + table, not the bars above ── */}
        {isRouteLocked() && !sessionUnlocked && (
          <div
            onClick={() => {
              setShowLockModal(true);
            }}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(11,12,14,0.92)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              zIndex: 8500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 20,
                padding: "40px 32px",
                background: "#141518",
                border: "1px solid rgba(232,85,32,0.35)",
                borderRadius: 22,
                boxShadow: "0 16px 70px rgba(0,0,0,0.85)",
                maxWidth: 360,
                width: "90%",
                textAlign: "center",
              }}
            >
              <img
                src="/img/SGK_logo.png"
                alt="SGK Home Delivery"
                style={{ height: 52, width: "auto" }}
              />
              <div
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: "50%",
                  background: "rgba(232,85,32,0.1)",
                  border: "2px solid rgba(232,85,32,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 30px rgba(232,85,32,0.2)",
                }}
              >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    stroke="#E85520"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 11V7a5 5 0 0 1 10 0v4"
                    stroke="#E85520"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: 24,
                    fontWeight: 800,
                    color: "#e8e9eb",
                    textTransform: "uppercase",
                    letterSpacing: "1.5px",
                    marginBottom: 10,
                  }}
                >
                  Routes Locked
                </div>
                <div
                  style={{ fontSize: 13, color: "#7a7d85", lineHeight: 1.7 }}
                >
                  Routes are locked after{" "}
                  <strong style={{ color: "#e8e9eb" }}>5:00 PM UK time</strong>.
                  <br />
                  Enter your admin passcode to make changes.
                </div>
              </div>
              <button
                onClick={() => setShowLockModal(true)}
                style={{
                  width: "100%",
                  padding: "14px",
                  background: "#E85520",
                  border: "none",
                  borderRadius: 12,
                  color: "#000",
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 13,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 11V7a5 5 0 0 1 10 0v4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Enter Passcode
              </button>
              <div style={{ fontSize: 11, color: "#454850" }}>
                SGK FleetOps · Route Security
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Status Overlay */}
      {statusOverlay &&
        (() => {
          const isDriver =
            statusOverlay === "driversAccepted" ||
            statusOverlay === "driversPending";
          const isAccepted =
            statusOverlay === "driversAccepted" ||
            statusOverlay === "sidemenAccepted";
          const names = isDriver
            ? savedCrews
                .filter((c) =>
                  isAccepted ? c.driver.confirmed : !c.driver.confirmed,
                )
                .map((c) => c.driver.name)
            : savedCrews
                .filter((c) =>
                  isAccepted ? c.sideman.confirmed : !c.sideman.confirmed,
                )
                .map((c) => c.sideman.name);
          const title =
            statusOverlay === "driversAccepted"
              ? `Drivers Accepted (${names.length})`
              : statusOverlay === "sidemenAccepted"
                ? `Sidemen Accepted (${names.length})`
                : statusOverlay === "driversPending"
                  ? `Drivers Pending (${names.length})`
                  : `Sidemen Pending (${names.length})`;
          const col = isAccepted ? "#3ecf6e" : "#e05252";
          return (
            <div
              onClick={() => setStatusOverlay(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.75)",
                zIndex: 9000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#141518",
                  border: `1px solid ${col}40`,
                  borderRadius: 16,
                  padding: 24,
                  minWidth: 280,
                  maxWidth: 360,
                  maxHeight: "70vh",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: 20,
                    fontWeight: 800,
                    color: col,
                    marginBottom: 16,
                  }}
                >
                  {title}
                </div>
                {names.length === 0 ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#454850",
                      fontStyle: "italic",
                    }}
                  >
                    None
                  </div>
                ) : (
                  <div
                    style={{
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    {names.map((n, i) => (
                      <div
                        key={i}
                        style={{
                          background: "#1c1e22",
                          border: "1px solid rgba(255,255,255,0.07)",
                          borderRadius: 8,
                          padding: "8px 14px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#e8e9eb",
                        }}
                      >
                        {n}
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setStatusOverlay(null)}
                  style={{
                    marginTop: 16,
                    padding: "9px",
                    background: col,
                    border: "none",
                    borderRadius: 8,
                    color: "#000",
                    fontFamily: "'Barlow',sans-serif",
                    fontSize: 12,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          );
        })()}

      {/* Lock Modal */}
      {showLockModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowLockModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#141518",
              border: "1px solid rgba(232,85,32,0.4)",
              borderRadius: 16,
              padding: "28px 24px",
              minWidth: 300,
              boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
            }}
          >
            <div
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: 18,
                fontWeight: 800,
                color: "#e8e9eb",
                marginBottom: 6,
              }}
            >
              🔒 Routes Locked
            </div>
            <div style={{ fontSize: 12, color: "#7a7d85", marginBottom: 18 }}>
              Routes for today are locked after 5:00 PM. Enter the admin
              password to make changes.
            </div>
            <input
              type="password"
              value={lockInput}
              onChange={(e) => setLockInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (lockInput === lockPassword) {
                    setShowLockModal(false);
                    setSessionUnlocked(true);
                    if (pendingAction) {
                      pendingAction();
                      setPendingAction(null);
                    }
                  } else {
                    showToast("Incorrect password");
                    setLockInput("");
                  }
                }
              }}
              placeholder="Enter password"
              autoFocus
              style={{
                width: "100%",
                background: "#1c1e22",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                padding: "10px 12px",
                color: "#e8e9eb",
                fontFamily: "'Barlow',sans-serif",
                fontSize: 14,
                outline: "none",
                marginBottom: 12,
                boxSizing: "border-box",
              }}
            />
            <button
              onClick={() => {
                if (lockInput === lockPassword) {
                  setShowLockModal(false);
                  setSessionUnlocked(true);
                  if (pendingAction) {
                    pendingAction();
                    setPendingAction(null);
                  }
                } else {
                  showToast("Incorrect password");
                  setLockInput("");
                }
              }}
              style={{
                width: "100%",
                padding: "11px",
                background: "#E85520",
                border: "none",
                borderRadius: 8,
                color: "#000",
                fontFamily: "'Barlow',sans-serif",
                fontSize: 13,
                fontWeight: 800,
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Unlock
            </button>
          </div>
        </div>
      )}

      {/* Profile Overlay */}
      {overlayProfileId &&
        (() => {
          const op = getP(overlayProfileId);
          if (!op) return null;
          return (
            <div
              onClick={() => setOverlayProfileId(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.7)",
                zIndex: 9000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#141518",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 16,
                  padding: "28px 24px",
                  minWidth: 280,
                  maxWidth: 360,
                  boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background: "#24262c",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#E85520",
                      flexShrink: 0,
                      overflow: "hidden",
                      border: "2px solid rgba(232,85,32,0.3)",
                    }}
                  >
                    {op.photo ? (
                      <img
                        src={op.photo}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        alt=""
                      />
                    ) : (
                      (op.firstName || "?").charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: "'Barlow Condensed',sans-serif",
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#e8e9eb",
                      }}
                    >
                      {op.firstName}
                      {op.lastName ? " " + op.lastName : ""}
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 10px",
                        borderRadius: 20,
                        background:
                          op.role === "driver"
                            ? "rgba(232,85,32,0.15)"
                            : "rgba(62,207,110,0.15)",
                        border: `1px solid ${op.role === "driver" ? "rgba(232,85,32,0.3)" : "rgba(62,207,110,0.3)"}`,
                        color: op.role === "driver" ? "#E85520" : "#3ecf6e",
                        textTransform: "uppercase",
                      }}
                    >
                      {op.role}
                    </span>
                  </div>
                </div>
                {[
                  { l: "User ID", v: (op as any).age || "—" },
                  { l: "Phone", v: op.phone || "—" },
                  { l: "DOB", v: op.dob || "—" },
                ].map((r) => (
                  <div
                    key={r.l}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "9px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: "#7a7d85",
                        fontWeight: 600,
                      }}
                    >
                      {r.l}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        color: "#e8e9eb",
                        fontWeight: 700,
                      }}
                    >
                      {r.v}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    marginTop: 16,
                    textAlign: "center",
                    fontSize: 11,
                    color: "#454850",
                  }}
                >
                  Click anywhere outside to close
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

// ─── Note Cell — pencil icon that expands to textarea ───────────
interface NoteCellProps {
  note: string;
  label: string;
  onChange: (v: string) => void;
}
const NoteCell: React.FC<NoteCellProps> = ({ note, label, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const hasNote = note.trim().length > 0;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        title={hasNote ? `${label}: ${note}` : `Add ${label}`}
        style={{
          width: 26,
          height: 26,
          borderRadius: 5,
          background: "none",
          border: "none",
          color: hasNote ? "#3ecf6e" : "#454850",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 0.15s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.color = hasNote ? "#3ecf6e" : "#E85520")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.color = hasNote ? "#3ecf6e" : "#454850")
        }
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            zIndex: 20,
            background: "#1c1e22",
            border: `1px solid ${hasNote ? "rgba(62,207,110,0.4)" : "rgba(232,85,32,0.4)"}`,
            borderRadius: 8,
            padding: 8,
            minWidth: 220,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#7a7d85",
              marginBottom: 5,
            }}
          >
            {label}
          </div>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Add a note for this ${label.toLowerCase()}…`}
            rows={3}
            style={{
              width: "100%",
              background: "#141518",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 6,
              padding: "7px 9px",
              color: "#e8e9eb",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 12,
              outline: "none",
              resize: "none",
              boxSizing: "border-box",
              lineHeight: 1.5,
            }}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            {hasNote && (
              <button
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                style={{
                  flex: 1,
                  padding: "6px",
                  background: "transparent",
                  border: "1px solid rgba(224,82,82,0.3)",
                  borderRadius: 6,
                  color: "#e05252",
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              style={{
                flex: 2,
                padding: "6px",
                background: "#E85520",
                border: "none",
                borderRadius: 6,
                color: "#000",
                fontFamily: "'Barlow',sans-serif",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Drop Zone ────────────────────────────────────────────────
interface DZProps {
  profile: Profile | null;
  isOver: boolean;
  dot: DotStatus;
  label: string;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onClear: () => void;
  onNameClick?: () => void;
  onTableDragStart?: (e: React.DragEvent) => void;
}
const DropZone: React.FC<DZProps> = ({
  profile,
  isOver,
  dot,
  label,
  onDragOver,
  onDragLeave,
  onDrop,
  onClear,
  onNameClick,
  onTableDragStart,
}) => {
  const d = DOT[dot];
  return (
    <div style={{ padding: "3px 5px" }}>
      <div
        style={{
          minHeight: 40,
          borderRadius: 7,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 10px",
          transition: "all 0.15s",
          border: `1px ${profile ? "solid" : "dashed"} ${isOver ? "#E85520" : "rgba(255,255,255,0.1)"}`,
          background: isOver ? "rgba(232,85,32,0.1)" : "transparent",
          boxShadow: isOver ? "0 0 0 2px rgba(232,85,32,0.2)" : "none",
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            flexShrink: 0,
            background: d.bg,
            boxShadow: d.shadow,
            transition: "background 0.4s, box-shadow 0.4s",
          }}
          title={d.title}
        />

        {profile ? (
          <>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: "#24262c",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#E85520",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {profile.photo ? (
                <img
                  src={profile.photo}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                (profile.firstName || "?").charAt(0).toUpperCase()
              )}
            </div>
            <div
              draggable={!!onTableDragStart}
              onDragStart={onTableDragStart}
              onClick={onNameClick}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: onNameClick ? "#E85520" : "#e8e9eb",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: onTableDragStart
                  ? "grab"
                  : onNameClick
                    ? "pointer"
                    : "default",
                textDecoration: onNameClick ? "underline" : "none",
              }}
            >
              {(profile as any).username ||
                `${profile.firstName}${profile.lastName ? " " + profile.lastName : ""}`}
            </div>
            {(profile as any).isFormerEmployee && (
              <div
                title="No longer with the company"
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: "#e05252",
                  background: "rgba(224,82,82,0.12)",
                  border: "1px solid rgba(224,82,82,0.35)",
                  borderRadius: 5,
                  padding: "2px 6px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                ⚠ Former
              </div>
            )}
            <button
              onClick={onClear}
              style={{
                background: "none",
                border: "none",
                color: "#454850",
                cursor: "pointer",
                fontSize: 16,
                padding: "0 2px",
                lineHeight: 1,
                flexShrink: 0,
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#e05252")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#454850")}
            >
              ×
            </button>
          </>
        ) : (
          <span
            style={{
              fontSize: 11,
              color: "#454850",
              fontWeight: 500,
              pointerEvents: "none",
            }}
          >
            + {label}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Draggable profile chip ────────────────────────────────────
interface ChipProps {
  p: Profile;
  dragging: boolean;
  assigned: boolean;
  onStart: (e: React.DragEvent, p: Profile) => void;
  onEnd: () => void;
}
const Chip: React.FC<ChipProps> = ({
  p,
  dragging,
  assigned,
  onStart,
  onEnd,
}) => {
  const name =
    (p as any).username ||
    `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
  return (
    <div
      draggable
      onDragStart={(e) => onStart(e, p)}
      onDragEnd={onEnd}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "6px 8px",
        background: dragging ? "rgba(255,255,255,0.02)" : "#1c1e22",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 7,
        cursor: "grab",
        opacity: dragging ? 0.4 : 1,
        userSelect: "none",
        transition: "all 0.15s",
      }}
      title={`Drag to assign ${name}`}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: "#24262c",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          fontWeight: 700,
          color: "#E85520",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {p.photo ? (
          <img
            src={p.photo}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          (p.firstName || "?").charAt(0).toUpperCase()
        )}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: assigned ? "#3ecf6e" : "#e8e9eb",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </div>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="rgba(255,255,255,0.2)"
      >
        <circle cx="9" cy="5" r="1.5" />
        <circle cx="15" cy="5" r="1.5" />
        <circle cx="9" cy="12" r="1.5" />
        <circle cx="15" cy="12" r="1.5" />
        <circle cx="9" cy="19" r="1.5" />
        <circle cx="15" cy="19" r="1.5" />
      </svg>
    </div>
  );
};

// ─── Start Time Picker ────────────────────────────────────────
interface STPProps {
  value: string;
  onChange: (v: string) => void;
}
const StartTimePicker: React.FC<STPProps> = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const parts = value.split(":").map(Number);
  const h24 = parts[0] || 7;
  const m = parts[1] || 0;
  const isPM = h24 >= 12;
  const h12 = h24 % 12 || 12;
  const to24 = (h: number, min: number, pm: boolean) =>
    String((h % 12) + (pm ? 12 : 0)).padStart(2, "0") +
    ":" +
    String(min).padStart(2, "0");
  const display =
    h12 + ":" + String(m).padStart(2, "0") + " " + (isPM ? "PM" : "AM");
  return (
    <div style={{ padding: "4px 6px", position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: "transparent",
          border: "1px solid transparent",
          borderRadius: 5,
          padding: "6px 4px",
          color: "#7a7d85",
          fontFamily: "'Barlow',sans-serif",
          fontSize: 12,
          fontWeight: 600,
          outline: "none",
          cursor: "pointer",
          transition: "all 0.15s",
          textAlign: "center",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#1c1e22";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
          e.currentTarget.style.color = "#e8e9eb";
        }}
        onMouseLeave={(e) => {
          if (!open) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "transparent";
            e.currentTarget.style.color = "#7a7d85";
          }
        }}
      >
        {display}
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 30,
            background: "#1c1e22",
            border: "1px solid rgba(232,85,32,0.4)",
            borderRadius: 8,
            padding: 8,
            minWidth: 170,
            boxShadow: "0 4px 20px rgba(0,0,0,0.6)",
          }}
        >
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#7a7d85",
                  marginBottom: 4,
                }}
              >
                Hour
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 3,
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hr) => (
                  <button
                    key={hr}
                    onClick={() => onChange(to24(hr, m, isPM))}
                    style={{
                      padding: "5px 2px",
                      borderRadius: 4,
                      border: "none",
                      background: hr === h12 ? "#E85520" : "#0d0e10",
                      color: hr === h12 ? "#000" : "#e8e9eb",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {hr}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: 46 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#7a7d85",
                  marginBottom: 4,
                }}
              >
                Min
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {[0, 15, 30, 45].map((min) => (
                  <button
                    key={min}
                    onClick={() => onChange(to24(h12, min, isPM))}
                    style={{
                      padding: "5px 4px",
                      borderRadius: 4,
                      border: "none",
                      background: min === m ? "#E85520" : "#0d0e10",
                      color: min === m ? "#000" : "#e8e9eb",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {String(min).padStart(2, "0")}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={() => onChange(to24(h12, m, !isPM))}
            style={{
              width: "100%",
              padding: "7px",
              borderRadius: 6,
              border: "none",
              background: isPM
                ? "rgba(232,85,32,0.15)"
                : "rgba(62,207,110,0.15)",
              color: isPM ? "#E85520" : "#3ecf6e",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 6,
            }}
          >
            {isPM ? "PM — tap for AM" : "AM — tap for PM"}
          </button>
          <button
            onClick={() => setOpen(false)}
            style={{
              width: "100%",
              padding: "6px",
              borderRadius: 6,
              border: "none",
              background: "#E85520",
              color: "#000",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              textTransform: "uppercase",
            }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default RouteBuilder;
